/**
 * Sign in or sign up, one act.
 *
 * A phone number, one code, and for a number that has no account yet a name.
 * Nothing else: no person type, no کد ملی. Those belong to the profile, which a
 * member completes only when they want to trade.
 *
 * The code is asked for with `signUp`, so the server answers a proved number
 * either with a session or, when it has no account, with a registration
 * ticket. The account is never looked up before the code is proved, so the
 * panel cannot be used to find out who is a member.
 *
 * One implementation, three callers: the modal any page opens, the sign in
 * page, and the first step of the complete-profile flow for a visitor who is
 * not signed in.
 */

import { useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, ErrorText, FIELD_EXAMPLE, Field, Input, useFormErrors } from '@metal/ui'
import { ApiError, api } from '@metal/ui/api'
import { useMarketClock, useSession } from '@metal/ui/session'
import { isMobileShaped, mmss, OtpPanel, type OtpChallenge } from '@metal/ui/otp'

interface VerifyAnswer {
  accessToken?: string
  registrationTicket?: string
  needsName?: boolean
}

interface ConsentDocument {
  documentKey: string
  titleFa: string
  version: string
  bodyFa: string
}

export interface AuthPanelProps {
  /** Called once the session is set. `signedUp` is true for a new account. */
  onDone: (result: { signedUp: boolean }) => void
  /** The page draws its own heading; the modal has one in its frame. */
  heading?: string
  /** A line the caller was sent here with, drawn above the number. */
  notice?: string | null
  initialMobile?: string
}

type Phase = 'phone' | 'code' | 'name'

/**
 * The number as typed, pasted or filled in by the browser, in the 09 form:
 * «+98 912 123 4567», «00989121234567» and «912 123 4567» become
 * «09121234567».
 */
function nationalMobile(raw: string): string {
  const d = raw.replace(/\D/g, '')
  if (/^98\d{10}$/.test(d)) return `0${d.slice(2)}`
  if (/^0098\d{10}$/.test(d)) return `0${d.slice(4)}`
  if (/^9\d{9}$/.test(d)) return `0${d}`
  return d.slice(0, 11)
}

/** A document title that opens its text in place, drawn as a link. */
const DOC_LINK = {
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'var(--c-accent)',
  textDecoration: 'underline',
  cursor: 'pointer',
} as const

export function AuthPanel({ onDone, heading, notice, initialMobile = '' }: AuthPanelProps) {
  const { setAccessToken } = useSession()
  const clock = useMarketClock()
  const nowMs = clock.now.getTime()

  const [phase, setPhase] = useState<Phase>('phone')
  const [mobile, setMobile] = useState(initialMobile)
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null)
  const [ticket, setTicket] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [failure, setFailure] = useState<ApiError | null>(null)
  const [staffNumber, setStaffNumber] = useState(false)
  const [busy, setBusy] = useState(false)
  const [throttledUntil, setThrottledUntil] = useState<number | null>(null)
  const [openDoc, setOpenDoc] = useState<string | null>(null)
  const scope = useRef<HTMLDivElement>(null)
  const docBodyId = useId()

  const throttledFor =
    throttledUntil === null ? 0 : Math.max(0, Math.ceil((throttledUntil - nowMs) / 1000))
  const shaped = isMobileShaped(mobile)
  const named = name.trim().length >= 2

  // A field says what is wrong once the person has left it; a press of the
  // button (or Enter) checks it, says so in a toast and focuses it.
  const form = useFormErrors<'mobile' | 'name'>(
    () => ({
      mobile: !shaped && (mobile ? 'شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود.' : 'شماره موبایل را وارد کنید.'),
      name: phase === 'name' && !named && 'نام و نام خانوادگی را کامل وارد کنید.',
    }),
    { scope },
  )

  // The two documents a new account accepts, at the version published now.
  const docs = useQuery({
    queryKey: ['consent-documents', 2],
    queryFn: () => api.get<{ items: ConsentDocument[] }>('/consents/documents', { step: 2 }),
    enabled: phase === 'name',
  })

  const request = async (): Promise<OtpChallenge> =>
    api.post<OtpChallenge>(
      '/auth/otp/request',
      { mobile, purpose: 'LOGIN', signUp: true },
      { raw: true },
    )

  const start = async () => {
    if (busy || throttledFor > 0) return
    if (!form.submit()) return
    setBusy(true)
    setFailure(null)
    try {
      setChallenge(await request())
      setPhase('code')
    } catch (e) {
      if (e instanceof ApiError) {
        setFailure(e)
        if (e.retryAfterSeconds !== null) setThrottledUntil(nowMs + e.retryAfterSeconds * 1000)
      }
    } finally {
      setBusy(false)
    }
  }

  const verify = async (code: string, challengeId: string) => {
    try {
      const done = await api.post<VerifyAnswer>(
        '/auth/otp/verify',
        { challengeId, code },
        { raw: true },
      )
      if (done.accessToken) {
        await setAccessToken(done.accessToken)
        onDone({ signedUp: false })
        return
      }
      if (done.registrationTicket) {
        setTicket(done.registrationTicket)
        setPhase('name')
      }
    } catch (e) {
      // A staff number: the code was right and is simply not enough. Staff
      // sign in on the operator console, with a password and a second factor.
      if (e instanceof ApiError && e.is('MFA_REQUIRED')) {
        setChallenge(null)
        setPhase('phone')
        setStaffNumber(true)
        return
      }
      throw e
    }
  }

  const create = async () => {
    if (busy || docs.isLoading) return
    if (!form.submit()) return
    if (!ticket) return
    setBusy(true)
    setFailure(null)
    try {
      const items = docs.data?.items ?? []
      const created = await api.post<{ accessToken?: string }>(
        '/registration/basic',
        {
          name: name.trim(),
          mobile,
          registrationTicket: ticket,
          consents: items.map((d) => ({
            documentKey: d.documentKey,
            version: d.version,
            accepted: true,
          })),
        },
        { raw: true },
      )
      if (created.accessToken) {
        await setAccessToken(created.accessToken)
        onDone({ signedUp: true })
      }
    } catch (e) {
      if (e instanceof ApiError) {
        // A spent or stale ticket means starting the number again.
        if (e.code.startsWith('REGISTRATION_TICKET')) {
          setTicket(null)
          setPhase('phone')
        }
        setFailure(e)
      }
    } finally {
      setBusy(false)
    }
  }

  if (phase === 'code' && challenge) {
    return (
      <OtpPanel
        challenge={challenge}
        onVerify={verify}
        onResend={request}
        onEdit={() => {
          setChallenge(null)
          setPhase('phone')
        }}
        submitLabel="ادامه"
      />
    )
  }

  if (phase === 'name') {
    const items = docs.data?.items ?? []
    const shown = items.find((d) => d.documentKey === openDoc)
    return (
      <div ref={scope} className="col" style={{ gap: 20 }}>
        <h2 className="t-h2">ساخت حساب</h2>

        <Field
          label="نام و نام خانوادگی"
          required
          error={
            form.errorOf('name') ??
            failure?.fields?.name ??
            (failure && !failure.fields ? failure.message : undefined)
          }
        >
          <Input
            autoFocus
            value={name}
            {...form.control('name')}
            invalid={form.invalidOf('name') || Boolean(failure)}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void create()}
          />
        </Field>

        <p className="t-sm t-sec" style={{ lineHeight: 1.9 }}>
          با ایجاد حساب،{' '}
          {items.map((d, i) => (
            <span key={d.documentKey}>
              {i > 0 && ' و '}
              <button
                type="button"
                style={DOC_LINK}
                aria-expanded={openDoc === d.documentKey}
                aria-controls={docBodyId}
                onClick={() => setOpenDoc((k) => (k === d.documentKey ? null : d.documentKey))}
              >
                {d.titleFa}
              </button>
            </span>
          ))}{' '}
          را می‌پذیرید.
        </p>

        {shown && (
          <div
            id={docBodyId}
            style={{
              background: 'var(--p-surface-2)',
              borderRadius: 'var(--r-md)',
              padding: '14px 16px',
            }}
          >
            <p className="t-sm t-sec" style={{ lineHeight: 2, whiteSpace: 'pre-line' }}>
              {shown.bodyFa}
            </p>
          </div>
        )}

        <Button
          variant="primary"
          size="lg"
          block
          loading={busy}
          disabled={docs.isLoading}
          onClick={() => void create()}
        >
          ساخت حساب و ورود
        </Button>
      </div>
    )
  }

  return (
    <div ref={scope} className="col" style={{ gap: 22 }}>
      {heading && <h1 className="t-h1">{heading}</h1>}

      {notice && (
        <p className="t-sm t-sec" role="status">
          {notice}
        </p>
      )}

      <div className="col" style={{ gap: 16 }}>
        <Field label="شماره موبایل" required error={form.errorOf('mobile')}>
          <Input
            numeric
            inputMode="numeric"
            autoComplete="tel-national"
            autoFocus
            dir="ltr"
            placeholder={FIELD_EXAMPLE.mobile}
            value={mobile}
            {...form.control('mobile')}
            onChange={(e) => {
              setMobile(nationalMobile(e.target.value))
              setStaffNumber(false)
              // A refusal of the number before is not about this one. A wait
              // stays until its countdown ends.
              setFailure((f) => (f && f.retryAfterSeconds !== null ? f : null))
            }}
            onKeyDown={(e) => e.key === 'Enter' && void start()}
          />
        </Field>

        {throttledFor > 0 ? (
          <div className="row-b">
            <span className="t-sm t-muted" role="status">
              {failure?.message}
            </span>
            <span className="t-sm t-muted num">{mmss(throttledFor)}</span>
          </div>
        ) : (
          // A refusal that only asked to wait is over once the countdown is.
          failure && failure.retryAfterSeconds === null && <ErrorText>{failure.message}</ErrorText>
        )}
        {staffNumber && <ErrorText>با این شماره از پنل اپراتور وارد شوید.</ErrorText>}
      </div>

      <Button
        variant="primary"
        size="lg"
        block
        loading={busy}
        disabled={throttledFor > 0}
        onClick={() => void start()}
      >
        دریافت کد
      </Button>
    </div>
  )
}
